import React, { useState, useEffect } from 'react'
import qs from 'query-string'
import { LocationDescriptor } from 'history'
import { useTransition } from 'react-spring'

import { routes } from 'constants/routes'

import {
  GetPublicWorkspaceFromTokenQueryResult,
  useGetPublicWorkspaceFromTokenQuery,
} from 'gqlTypes'

import {
  Description,
  WorkspaceLogo,
  Wrapper,
  ItemWrapper,
  InviterAvatar,
} from './RedirectBadge.styles'
import { normalizeToLocationObject } from 'utils/route'
import { Inviter } from 'modules/shares/AcceptShareInviteView/types'

const TRANSITION_DEFINITION = {
  from: { opacity: 0 },
  enter: { opacity: 1 },
  leave: { opacity: 0 },
  initial: null,
}

const getFirstArgument = (field: string | string[]) =>
  Array.isArray(field) ? field[0] : field

const getAnimationState = (
  loading: boolean,
  publicWorkspace: GetPublicWorkspaceFromTokenQueryResult,
  pathname?: string
) => {
  if (loading) {
    return 'loading' as const
  } else if (publicWorkspace.data?.getPublicWorkspaceFromToken) {
    return 'workspace-invite' as const
  } else if (
    routes.SHARE_ACCEPT_INVITE_SIGN_IN.template() === pathname ||
    routes.SHARE_ACCEPT_INVITE_SIGN_UP.template() === pathname
  ) {
    return 'document-invite' as const
  }
}

interface RedirectBadgeProps {
  from: LocationDescriptor
  documentInvitation?: {
    inviter: Inviter
    documentName: string
  }
}

const RedirectBadge: React.FC<RedirectBadgeProps> = ({
  from,
  documentInvitation,
}) => {
  const { pathname, search } = normalizeToLocationObject(from)

  const query = qs.parse(search || '')

  const publicWorkspace = useGetPublicWorkspaceFromTokenQuery({
    skip: routes.WORKSPACE_INVITE.template() !== pathname,
    variables: { inviteToken: getFirstArgument(query.token || '') },
  })

  // Reunite the loading states in case we add more badges in the future
  const loading = publicWorkspace.loading

  const [currentState, setState] = useState(
    /**
     *  We calculate the initial animation state, to prevent a reanimation
     *  from happening, the result might already be in cache when this
     *  component mounts
     */
    getAnimationState(loading, publicWorkspace, pathname)
  )

  const transitions = useTransition(
    currentState,
    item => item,
    TRANSITION_DEFINITION
  )

  useEffect(() => {
    setState(getAnimationState(loading, publicWorkspace, pathname))
  }, [loading, publicWorkspace, pathname])

  if (!currentState) {
    return null
  }

  return (
    <Wrapper>
      {transitions.map(({ item, props, key }) => (
        <>
          {(() => {
            if (item === 'workspace-invite') {
              const { getPublicWorkspaceFromToken } = publicWorkspace.data!
              const workspace = getPublicWorkspaceFromToken

              return (
                <ItemWrapper key={key} style={props}>
                  <WorkspaceLogo
                    workspaceName={workspace.name}
                    src={workspace.avatar?.small}
                    size="32px"
                  />
                  <Description>
                    You’ve been invited to <b>{workspace.name}</b>
                  </Description>
                </ItemWrapper>
              )
            } else if (item === 'document-invite') {
              if (!documentInvitation) return

              const { inviter, documentName } = documentInvitation

              return (
                <ItemWrapper key={key} style={props}>
                  {inviter && (
                    <InviterAvatar
                      size="32px"
                      src={inviter?.avatar?.large || undefined}
                      name={
                        inviter.name || '?' /** Name should always be there */
                      }
                    />
                  )}
                  <Description>
                    <b>{inviter?.name}</b> wants to share the document &ldquo;
                    <b>{documentName}</b>&rdquo; with you
                  </Description>
                </ItemWrapper>
              )
            }
          })()}
        </>
      ))}
    </Wrapper>
  )
}

export default RedirectBadge
